import { Globe, Mail, Phone, User } from "lucide-react";
import type { SetupFlowDefinition } from "@/types/setup/flow";

/**
 * Clubs & associations — the first declarative setup flow.
 *
 * A low-friction lead-capture flow, told as a short story: the customer answers
 * four quick things about their club and watches its "front desk" assemble as
 * they go, then wakes it up. Our team builds the real genie by hand from those
 * details. Adding another segment is just a matter of dropping a sibling file
 * into `src/data/setup/flows/` and registering it — the cinematic runner
 * (intro → questions → finale) is reused as-is.
 */
export const clubsSetupFlow: SetupFlowDefinition = {
  meta: {
    slug: "clubs",
    name: "Clubs & Associations",
    tagline: "A front-desk genie for your members.",
    description:
      "Give us the club's website and a couple of contact details — we'll build a genie that answers members and visitors around the clock.",
    genieIcon: "chat",
    mascot: "/genie-mascots/role/receptionist.png",
    heroHeadline: "Your club's front desk, always on",
    setupMinutes: "Takes 30 seconds",
  },
  intro: {
    eyebrow: "Help Genie Setup",
    title: "Let's bring your club's front desk to life.",
    subtitle:
      "Four quick things about your club. You'll watch its front desk take shape as you answer — then we wake it up.",
    ctaLabel: "Start building",
    reassurance: "About 30 seconds. No account, no sign-up.",
    image: "/solutions-pages/clubs-associations/hero.webp",
  },
  questions: [
    {
      id: "website",
      label: "Club website",
      eyebrow: "Where it learns",
      title: "Where should your genie learn from?",
      subtitle:
        "Point it at your club's website — that's the first thing it reads, so it can answer members from day one.",
      placeholder: "northsideclub.org.nz",
      inputMode: "url",
      autoComplete: "url",
      icon: Globe,
      hint: "A public website is enough for us to get started.",
      requiredMessage: "Add the club website to continue.",
      deskSlot: "knowledge",
      deskRowLabel: "Learns from",
      reaction: "Reading your site…",
      validate: (value) => {
        const trimmed = value.trim();
        const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
        try {
          const url = new URL(normalized);
          return url.hostname.includes(".") || "Enter a valid website.";
        } catch {
          return "Enter a valid website.";
        }
      },
    },
    {
      id: "contactName",
      label: "Your name",
      eyebrow: "Who's building it",
      title: "Who's bringing it to life?",
      subtitle: "Just so we know who to talk to while we get your front desk ready.",
      placeholder: "Jordan Smith",
      inputMode: "text",
      autoComplete: "name",
      icon: User,
      hint: "First name is fine.",
      requiredMessage: "Add your name to continue.",
      deskSlot: "owner",
      deskRowLabel: "Looked after by",
      reaction: "Nice to meet you",
      validate: (value) => value.trim().length >= 2 || "Enter your name.",
    },
    {
      id: "phone",
      label: "Members line",
      eyebrow: "The members line",
      title: "What number do members already call?",
      subtitle:
        "Your genie points people to the right place — and can pick up this line when no one else can.",
      placeholder: "+64 9 123 4567",
      inputMode: "tel",
      autoComplete: "tel",
      icon: Phone,
      hint: "Use the number people already know.",
      requiredMessage: "Add the best phone number to continue.",
      deskSlot: "line",
      deskRowLabel: "Members line",
      reaction: "Line connected",
      validate: (value) => /^[+()\d\s-]{7,}$/.test(value.trim()) || "Enter a valid phone number.",
    },
    {
      id: "email",
      label: "Contact email",
      eyebrow: "Where it lands",
      title: "Where should we send it when it wakes up?",
      subtitle: "We'll email you the moment your genie is live and ready to try.",
      placeholder: "hello@northsideclub.org.nz",
      inputMode: "email",
      autoComplete: "email",
      icon: Mail,
      hint: "We'll only use this to get back to you.",
      requiredMessage: "Add the contact email to continue.",
      deskSlot: "inbox",
      deskRowLabel: "Updates go to",
      reaction: "Inbox linked",
      validate: (value) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) || "Enter a valid email address.",
    },
  ],
  finale: {
    eyebrow: "Ready",
    title: "Meet your club's front desk.",
    subtitle:
      "Everything's in place. Give the word and we'll wake it up — our team builds it from here and emails you when it's live.",
    ctaLabel: "Bring it to life",
    reassurance: "Usually ready within a few hours — often much sooner.",
    image: "/solutions-pages/clubs-associations/cta-background.webp",
  },
};
